// Thin client for /api/reports.  Submission lives in
// BugReportModal (inline fetch); listing + admin patch live here.

export type ReportKind = "bug" | "feature";
export type ReportPriority = "high" | "med" | "low";
export type ReportStatus = "open" | "addressed";

export type ReportUser = {
  id?: string;
  email: string | null;
  handle: string | null;
  displayName: string | null;
};

export type Report = {
  id: string;
  kind: ReportKind;
  priority: ReportPriority;
  title: string;
  body: string;
  status: ReportStatus;
  resolution: string | null;
  resolvedAt: string | null;
  createdAt: string;
  reporter: ReportUser | null;
  resolver: ReportUser | null;
};

function authHeaders(token: string): HeadersInit {
  return { Authorization: `Bearer ${token}` };
}

export async function fetchReports(token: string): Promise<Report[]> {
  const r = await fetch("/api/reports", { headers: authHeaders(token) });
  if (!r.ok) throw new Error(`reports ${r.status}`);
  return (await r.json()) as Report[];
}

// Single PATCH endpoint handles two intents:
//   - Triage (status/resolution) — admin-only on the server.
//   - Content (kind/priority/title/body) — reporter-only (or admin).
// The server validates per-field so a single call can do both atomically.
export type ReportPatch = {
  status?: ReportStatus;
  resolution?: string;
  kind?: ReportKind;
  priority?: ReportPriority;
  title?: string;
  body?: string;
};

export async function patchReport(
  token: string,
  id: string,
  patch: ReportPatch,
): Promise<void> {
  const r = await fetch(`/api/reports/${encodeURIComponent(id)}`, {
    method: "PATCH",
    headers: { ...authHeaders(token), "Content-Type": "application/json" },
    body: JSON.stringify(patch),
  });
  if (!r.ok) {
    const body = (await r.json().catch(() => null)) as
      | { error?: string }
      | null;
    throw new Error(body?.error ?? `HTTP ${r.status}`);
  }
}
